import { useCallback, useRef, useState } from "react";
import { apiUrl } from "../config";
import ENDPOINTS from "../api/endpoints";

/**
 * useInlineEdit — streams an inline AI code edit from /spark/edit-artifact.
 *
 * The backend yields newline-delimited JSON objects:
 *   {"type":"started"}
 *   {"type":"intent","text":"..."}            // non-selection edits, once
 *   {"type":"chunk","text":"..."}             // one or more
 *   {"type":"done","applied_count":N?}
 *   {"type":"apply_failed","reason":"...","message":"...",
 *    "failed_blocks":[...],"raw_response":"..."}   // soft, recoverable
 *   {"type":"error","message":"..."}                // hard failure
 *
 * Keepalive SSE comments (`: keepalive\n\n`) are silently skipped.
 *
 * @param {Object} callbacks
 * @param {function(string): void} callbacks.onChunk       - Called on every chunk with the accumulated buffer so far
 * @param {function(string): void} callbacks.onComplete    - Called once with the full text when done
 * @param {function(string): void} callbacks.onError       - Called with an error message on hard failure or cancellation
 * @param {function(string): void} [callbacks.onIntent]    - Called once with the AI's one-line intent summary
 * @param {function(object): void} [callbacks.onApplyFailed] - Called when SEARCH/REPLACE blocks fail to apply (soft failure with retry context)
 * @param {function(object): void} [callbacks.onUsage]       - Called once with token usage { tokens_input, tokens_output, tokens_total, tokens_cached? }
 *
 * @returns {{ startEdit, cancel, isEditing, error }}
 */
export default function useInlineEdit({ onChunk, onComplete, onError, onIntent, onApplyFailed, onUsage }) {
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsEditing(false);
  }, []);

  const startEdit = useCallback(
    async ({ prompt, currentCode, language, diagnostics = [], answerId, chatId, modelId, assetIds = [], selectionStart, selectionEnd }) => {
      // Cancel any in-flight request before starting a new one
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      setIsEditing(true);
      setError(null);

      const sessionToken = localStorage.getItem("session_token") || "";

      let response;
      try {
        response = await fetch(apiUrl + ENDPOINTS.EDIT_ARTIFACT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Session-Token": sessionToken,
          },
          body: JSON.stringify({
            current_code: currentCode,
            user_prompt: prompt,
            language,
            diagnostics,
            answer_id: answerId,
            chat_id: chatId,
            ...(modelId && { model_id: modelId }),
            ...(assetIds.length > 0 && { asset_ids: assetIds }),
            ...(selectionStart != null && { selection_start: selectionStart, selection_end: selectionEnd }),
          }),
          signal: controller.signal,
        });
      } catch (err) {
        if (err.name === "AbortError") return; // cancelled — no callback
        const msg = err.message || "Network error";
        setError(msg);
        setIsEditing(false);
        onError?.(msg);
        return;
      }

      if (!response.ok) {
        let msg = `Request failed (${response.status})`;
        try {
          const body = await response.json();
          if (body?.error) msg = body.error;
        } catch (_) {}
        setError(msg);
        setIsEditing(false);
        onError?.(msg);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";       // accumulated model text yielded to callbacks
      let lineBuffer = "";   // partial JSON line carried across reader reads

      // Process one complete JSON line. Returns true if the stream is finished
      // (done/error/apply_failed received) so the caller can break out of the read loop.
      const processLine = (line) => {
        const trimmed = line.trim();
        if (!trimmed) return false;
        if (trimmed.startsWith(":")) return false; // SSE keepalive comment
        let parsed;
        try {
          parsed = JSON.parse(trimmed);
        } catch (_) {
          return false; // malformed — skip
        }
        if (parsed.type === "chunk" && parsed.text) {
          // For SEARCH/REPLACE flow the backend emits a single chunk with the
          // full merged file — replace, don't append. We detect this by checking
          // whether the buffer is currently empty (first chunk after intent).
          // For the streaming/selection flow multiple chunks arrive and we
          // accumulate them. The replace-vs-append distinction does not matter
          // semantically because the SEARCH/REPLACE worker emits exactly one
          // chunk; concatenation with empty == replacement.
          buffer += parsed.text;
          onChunk?.(buffer);
        } else if (parsed.type === "intent" && parsed.text) {
          onIntent?.(parsed.text);
        } else if (parsed.type === "done") {
          onComplete?.(buffer);
          setIsEditing(false);
          abortRef.current = null;
          return true;
        } else if (parsed.type === "apply_failed") {
          // Soft failure: the model responded but blocks couldn't be applied.
          // Surface the structured payload so the UI can render an actionable
          // error panel with retry. We do NOT call onComplete (no good code to
          // show) and we DO call onError with the human message so existing
          // error toasts still fire.
          setIsEditing(false);
          abortRef.current = null;
          onApplyFailed?.(parsed);
          onError?.(parsed.message || "Edit could not be applied");
          return true;
        } else if (parsed.type === "error") {
          const msg = parsed.message || "Edit failed";
          setError(msg);
          setIsEditing(false);
          abortRef.current = null;
          onError?.(msg);
          return true;
        } else if (parsed.type === "usage") {
          onUsage?.(parsed);
        }
        // "started" — no action needed
        return false;
      };

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // Append decoded bytes to the line buffer. We MUST NOT parse until we
          // see a `\n` boundary, otherwise a JSON line that straddles two reads
          // will fail JSON.parse and be silently dropped (corrupting the output).
          lineBuffer += decoder.decode(value, { stream: true });

          // Split on `\n`; the last element is whatever follows the final `\n`
          // in this chunk and may be a partial line — keep it in the buffer.
          const parts = lineBuffer.split("\n");
          lineBuffer = parts.pop() ?? "";

          for (const line of parts) {
            if (processLine(line)) return;
          }
        }

        // Flush: decoder + any trailing partial line the server didn't terminate.
        lineBuffer += decoder.decode();
        if (lineBuffer) {
          if (processLine(lineBuffer)) return;
          lineBuffer = "";
        }

        // Stream ended without explicit "done" — treat accumulated buffer as complete
        if (buffer) {
          onComplete?.(buffer);
        }
      } catch (err) {
        if (err.name === "AbortError") return; // user cancelled
        const msg = err.message || "Stream read error";
        setError(msg);
        onError?.(msg);
      } finally {
        setIsEditing(false);
        abortRef.current = null;
      }
    },
    [onChunk, onComplete, onError, onIntent, onApplyFailed, onUsage]
  );

  return { startEdit, cancel, isEditing, error };
}
